import {
  applicantProfileSchema,
  applicationSchema,
  applyPostingRequestSchema,
  applyPostingResponseSchema,
  creditLedgerResponseSchema,
  employerApplicantCountsSchema,
  employerApplicantsListResponseSchema,
  myApplicationCountsSchema,
  myApplicationDetailSchema,
  myApplicationListResponseSchema,
  pendingApplicantsResponseSchema,
  inviteApplicationResponseSchema,
  rejectApplicationsBatchResponseSchema,
  type ApplyPostingRequest,
  type EmployerApplicantsQuery,
} from "@chotca/config";
import type { RequestContext } from "./http";
import { requestJson } from "./http";

export function createApplicationsResource(ctx: RequestContext) {
  return {
    /** Worker applies to a posting. */
    apply(postingId: string, body: ApplyPostingRequest = {}) {
      const payload = applyPostingRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/postings/${postingId}/apply`,
        body: payload,
        dataSchema: applyPostingResponseSchema,
        auth: true,
      });
    },

    /** Worker fetches a single application by id, including timeline events. */
    getMine(applicationId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/me/applications/${applicationId}`,
        dataSchema: myApplicationDetailSchema,
        auth: true,
      });
    },

    /** Badge counts for the worker's "Cần xử lý" / "Tất cả" tabs. */
    countMine() {
      return requestJson(ctx, {
        method: "GET",
        path: "/me/applications/counts",
        dataSchema: myApplicationCountsSchema,
        auth: true,
      });
    },

    listMine(state?: string, cursor?: string, postingId?: string) {
      const qs = new URLSearchParams();
      if (state) qs.set("state", state);
      if (cursor) qs.set("cursor", cursor);
      if (postingId) qs.set("postingId", postingId);
      const query = qs.toString();
      return requestJson(ctx, {
        method: "GET",
        path: `/me/applications${query ? `?${query}` : ""}`,
        dataSchema: myApplicationListResponseSchema,
        auth: true,
      });
    },

    /** Worker withdraws application. */
    withdraw(applicationId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/applications/${applicationId}/withdraw`,
        dataSchema: applicationSchema,
        auth: true,
      });
    },

    /**
     * Employer fetches the full applicant CV/profile (no credit cost).
     * Phone/email and full name are only present after the invite is committed.
     */
    getProfile(applicationId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/applications/${applicationId}/profile`,
        dataSchema: applicantProfileSchema,
        auth: true,
      });
    },

    /**
     * Employer invites worker (spends an interview credit, unlocks contact).
     * Idempotent — calling on an already-invited application returns the same
     * data without re-deducting credit.
     */
    invite(applicationId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/applications/${applicationId}/invite`,
        dataSchema: inviteApplicationResponseSchema,
        auth: true,
      });
    },

    /**
     * Employer dashboard "Cần xử lý" feed — applicants in APPLIED state
     * across all OPEN postings of the user's orgs. Items only disappear
     * once the employer invites or rejects them.
     */
    listPendingForMe(limit?: number) {
      const qs = limit ? `?limit=${limit}` : "";
      return requestJson(ctx, {
        method: "GET",
        path: `/me/pending-applicants${qs}`,
        dataSchema: pendingApplicantsResponseSchema,
        auth: true,
      });
    },

    /** Employer rejects an applicant. */
    reject(applicationId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/applications/${applicationId}/reject`,
        dataSchema: applicationSchema,
        auth: true,
      });
    },

    /**
     * Employer global applicant list ("Danh sách ứng viên") — tabbed, filtered
     * by location/search, page-number paginated.
     */
    listForEmployer(query: EmployerApplicantsQuery = {}) {
      return requestJson(ctx, {
        method: "GET",
        path: "/me/applicants",
        query,
        dataSchema: employerApplicantsListResponseSchema,
        auth: true,
      });
    },

    /** Interview-credit history (+/-) for the "Lượt phỏng vấn" card. */
    creditLedger(limit?: number) {
      return requestJson(ctx, {
        method: "GET",
        path: "/me/credit-ledger",
        query: limit ? { limit } : undefined,
        dataSchema: creditLedgerResponseSchema,
        auth: true,
      });
    },

    /** Tab badge counts for the employer applicant list. */
    applicantCounts(orgAddressId?: string) {
      return requestJson(ctx, {
        method: "GET",
        path: "/me/applicants/counts",
        query: orgAddressId ? { orgAddressId } : undefined,
        dataSchema: employerApplicantCountsSchema,
        auth: true,
      });
    },

    /** Employer rejects many applicants at once. */
    rejectBatch(applicationIds: string[]) {
      return requestJson(ctx, {
        method: "POST",
        path: "/applications/reject-batch",
        body: { applicationIds },
        dataSchema: rejectApplicationsBatchResponseSchema,
        auth: true,
      });
    },
  };
}
